import { postData } from '../utils/ajax-utils';
/* eslint-disable @typescript-eslint/no-explicit-any */
declare const script_var: any;

export default function initSearchAgenceForm(): void {
    document.querySelectorAll<HTMLFormElement>('.modal-agence').forEach($container => {
        new SearchAgenceForm($container);
    });
}

class SearchAgenceForm {
    $container: HTMLElement;
    $form: HTMLFormElement | null;
    $resultContainer: HTMLElement | null;
    $controller: AbortController;
    searchTimeout: number | null = null;
    searchDelay: number = 500; // Délai en ms avant de lancer la recherche
    minChars: number = 2; // Nombre minimum de caractères pour déclencher la recherche

    constructor($container: HTMLFormElement) {
        this.$container = $container;
        this.$resultContainer = this.$container.querySelector('.results');
        this.$form = this.$container.querySelector('form.search-form');
        this.$controller = new AbortController();
        
        this.initListeners();
    }

    initListeners(): void {
        this.$form?.addEventListener('submit', (event) => {
            event.preventDefault();
            this.submitForm();
        });

        // Capture les saisies dans l'input[search] pour recherche en temps réel
        const searchInput = this.$form?.querySelector<HTMLInputElement>('input[type="search"]');
        if (searchInput) {
            searchInput.addEventListener('input', () => {
                // Annuler le timeout précédent s'il existe
                if (this.searchTimeout !== null) {
                    clearTimeout(this.searchTimeout);
                }
                
                // Vérifier si l'utilisateur a saisi au moins 2 caractères
                const inputValue = searchInput.value.trim();
                if (inputValue.length >= 2) {
                    // Mettre en place un délai pour éviter trop de requêtes
                    this.searchTimeout = window.setTimeout(() => {
                        this.submitForm();
                    }, this.searchDelay);
                } else if (inputValue.length === 0) {
                    // Si le champ est vide, réinitialiser l'affichage sans délai
                    this.resetDisplay();
                }
            });
            
            searchInput.addEventListener('keypress', (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    // Vérifier si l'input a au moins 2 caractères
                    const inputValue = searchInput.value.trim();
                    if (inputValue.length >= 2) {
                        // Annuler le timeout en cours si l'utilisateur appuie sur Entrée
                        if (this.searchTimeout !== null) {
                            clearTimeout(this.searchTimeout);
                            this.searchTimeout = null;
                        }
                        this.submitForm();
                    }
                }
            });
        }
    }

    // Méthode pour réinitialiser l'affichage quand le champ est vide
    resetDisplay(): void {
        if (!this.$resultContainer) return;
        
        const agences = Array.from(this.$resultContainer.querySelectorAll<HTMLLIElement>('li.agence') || []);
        
        // Afficher toutes les agences
        agences.forEach(agence => agence.classList.remove('hidden'));
        
        // Trier les agences selon leur position d'origine (attribut data-position)
        const agencesTriees = agences.sort((a, b) => {
            const posA = parseInt(a.dataset.position || '0', 10);
            const posB = parseInt(b.dataset.position || '0', 10);
            return posA - posB;
        });
        
        // Réorganiser les éléments dans le DOM selon leur position d'origine
        agencesTriees.forEach(agence => {
            this.$resultContainer?.appendChild(agence);
        });
    }

    async submitForm(): Promise<void> {
        if (!this.$resultContainer) return;
        
        const searchInput = this.$form?.querySelector<HTMLInputElement>('input[type="search"]');
        const inputValue = searchInput?.value.trim() || '';
        
        // Vérifier à nouveau si la valeur a au moins le nombre minimum de caractères
        if (inputValue.length < this.minChars) {
            return;
        }

        // Annuler la requête précédente si elle est en cours
        this.$controller.abort();
        this.$controller = new AbortController();

        const formData = new URLSearchParams();
        formData.append('action', 'search_cp');
        
        const formElements = this.$form?.elements;

        if (formElements) {
            for (const element of Array.from(formElements)) {
                const input = element as HTMLInputElement | HTMLSelectElement;
                
                if (!input.name || input.disabled) continue;
            
                if (input instanceof HTMLSelectElement) {
                    if (input.value) {
                        formData.append(input.name, input.value);
                    }
                } else if (input instanceof HTMLInputElement) {
                    if (input.type === "checkbox" || input.type === "radio")  {
                        if (input.checked) {
                            formData.append(input.name, input.value);
                        }
                    } else {
                        // Gérer les autres inputs
                        formData.append(input.name, input.value);
                    }
                }
            }
        }
        
        postData(script_var.ajax_url, formData).then(response => {
            if (response.success && response.data.length > 0) {
                const searchInput = this.$form?.querySelector<HTMLInputElement>('input[type="search"]');
                const codePostalSaisi = searchInput?.value.trim();
                
                const agences = Array.from(this.$resultContainer?.querySelectorAll<HTMLLIElement>('li.agence') || []);
                if (!agences.length) return;
                
                // Si aucun code postal saisi, afficher toutes les agences sans tri particulier
                if (!codePostalSaisi) {
                    agences.forEach(agence => agence.classList.remove('hidden'));
                    return;
                }
                
                // Récupérer les coordonnées du code postal saisi
                const { latitude, longitude } = response.data[0];
                const lat1 = parseFloat(latitude);
                const lon1 = parseFloat(longitude);
                
                if (isNaN(lat1) || isNaN(lon1)) {
                    // Afficher toutes les agences sans tri si on n'a pas de coordonnées valides
                    agences.forEach(agence => agence.classList.remove('hidden'));
                    return;
                }
                
                // Calculer la distance pour chaque agence
                const agencesWithDistance = agences
                    .map(agence => {
                        const lat2 = parseFloat(agence.dataset.lat || '0');
                        const lon2 = parseFloat(agence.dataset.long || '0');
                        const distance = this.haversineDistance(lat1, lon1, lat2, lon2);
                        
                        return { agence, distance };
                    })
                    .filter(a => !isNaN(a.distance));
                
                // Trier par distance croissante
                agencesWithDistance.sort((a, b) => a.distance - b.distance);
                
                // Récupérer toutes les agences triées par proximité
                const agencesTriees = agencesWithDistance.map(a => a.agence);
                
                // S'assurer que toutes les agences sont visibles
                agences.forEach(agence => agence.classList.remove('hidden'));
                
                // Réordonner les éléments dans le DOM selon leur proximité
                agencesTriees.forEach(agence => {
                    this.$resultContainer?.appendChild(agence);
                });
            }
        }).catch(error => {
            // Gestion des erreurs de requête
            if (error.name !== 'AbortError') {
                console.error('Erreur lors de la recherche:', error);
            }
        });
    }

    haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371e3;
        const p1 = lat1 * Math.PI/180;
        const p2 = lat2 * Math.PI/180;
        const deltaP = p2 - p1;
        const deltaLon = lon2 - lon1;
        const deltaLambda = (deltaLon * Math.PI) / 180;
        const a = Math.sin(deltaP/2) * Math.sin(deltaP/2) +
                    Math.cos(p1) * Math.cos(p2) *
                    Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
        const d = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)) * R;
        return d;
    }
}